import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Button } from '@/components/Button/Button';
import { ConfirmModal, ConfirmModalHint } from '@/components/ConfirmModal/ConfirmModal';
import { isValidForm } from '@/helpers/formHelpers/formHelpers';
import { AssignUserFormWrapper, FormButtonBlock, FormMessage, FormTitle } from '../../../../shared/ui/styles';
import { USER_ROLE_MAP } from '@/const/user';
import { loadUserInfo } from '@/bundle/shared/api';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import {
  ASSIGN_AMPLITUDE_EVENTS_REDIRECT_MAP,
  ASSIGN_AMPLITUDE_EVENTS_SUCCESS_MAP,
  ERROR_ASSIGN_AMPLITUDE_EVENTS_REDIRECT_MAP,
} from '@/bundle/_OrgAdmin/pages/InboundWire/const/amplitudeEventsMap';
import { ASSIGN_USER_INITIAL_VALUES, ASSIGN_USER_SCHEMA } from '@/bundle/_OrgAdmin/shared/const/const';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { getIsAvailableToAssign } from '@/helpers/userHelpers';
import { Notification } from '@/components/Notification/Notification';
import { getExcludedResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { useAssignUser } from '@/bundle/shared/hooks/useAssignUser';
import { SearchUserEmailInput } from '@/bundle/shared/components/SearchUserEmailInput/SearchUserEmailInput';
import { TRIAL_MODE_SUBSCRIPTION_REQUIRED_ERROR_MESSAGE, HTTP_BAD_REQUEST_STATUS } from '@/api/const';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import {
  assignDepositor,
  createDepositor,
  depositorAssignmentStatus,
} from '@/bundle/_OrgAdmin/pages/InboundWire/api/api';
import { CreateDepositorPayloadType } from '@/api/v1/organization/wires/createDepositorApi';
import { useSubscriptionRequiredError } from '@/bundle/shared/hooks/useSubscriptionRequiredError';
import { SubscriptionRequiredModal } from '@/bundle/shared/components/modals/SubscriptionRequiredModal';
import { getAcceptMasterServiceAgreementPath } from '@/bundle/Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/path';
import { useNavigate } from 'react-router';

type AssignDepositorFormType = {
  wireId: string;
  isOpenConfirmModal: boolean;
  onToggleConfirmModal: (isOpen: boolean) => void;
  onSuccess: () => void;
};

export const AssignDepositorForm = ({
  wireId,
  isOpenConfirmModal,
  onToggleConfirmModal,
  onSuccess,
}: AssignDepositorFormType) => {
  const navigate = useNavigate();
  const { isViewModeSubscription } = useSubscriptionStatus();
  const [submittedEmailRequest, setSubmittedEmailRequest] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<null | string>(null);
  const { hasSubscriptionRequiredError, resetSubscriptionRequiredError, handleSubscriptionRequiredError } =
    useSubscriptionRequiredError();

  const { data: assignmentStatusData } = useQuery({
    queryKey: ['load_assignment_status', assignedUserId],
    queryFn: () => depositorAssignmentStatus(assignedUserId),
    enabled: !!assignedUserId,
  });

  const { data: loadUserData } = useQuery({
    queryKey: ['load_user', assignedUserId],
    queryFn: () => loadUserInfo(assignedUserId),
    enabled: !!assignedUserId && !!assignmentStatusData?.body?.is_available_to_assign,
  });

  const {
    data: createDepositorData,
    mutate: createDepositorMutate,
    isPending: isPendingCreateDepositor,
    reset: resetCreateDepositor,
  } = useMutation({
    mutationKey: ['create_depositor'],
    mutationFn: (newDepositorData: CreateDepositorPayloadType) => {
      return createDepositor(wireId, newDepositorData);
    },
    onSuccess(response) {
      if (response?.error) {
        return handleSubscriptionRequiredError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateNewDepositorSuccess);

      onSuccess();
    },
    onSettled() {
      onToggleConfirmModal(false);
    },
  });

  const {
    data: assignDepositorData,
    mutate: assignDepositorMutate,
    isPending: isPendingAssignDepositor,
    reset: resetAssignDepositor,
  } = useMutation({
    mutationKey: ['assign_depositor'],
    mutationFn: () => {
      const userData = loadUserData?.body;

      return assignDepositor(wireId, userData?.id);
    },
    onSuccess(response) {
      if (response?.error) {
        return handleSubscriptionRequiredError(response?.error);
      }

      const currentStatus = assignmentStatusData?.body?.code;

      amplitudeService.logEvent(ASSIGN_AMPLITUDE_EVENTS_SUCCESS_MAP[currentStatus]);

      onSuccess();
    },
    onSettled() {
      onToggleConfirmModal(false);
    },
  });

  const isAvailableToAssign = getIsAvailableToAssign(assignmentStatusData?.body?.is_available_to_assign);
  const user = loadUserData?.body;

  const createDepositorError = createDepositorData?.error;
  const assignRecipientError = assignDepositorData?.error;
  const apiError = createDepositorError || assignRecipientError;
  const hasApiError = apiError?.statusCode === HTTP_BAD_REQUEST_STATUS;

  const isLoading = isPendingAssignDepositor || isPendingCreateDepositor;
  const formError = getExcludedResponseError(apiError, TRIAL_MODE_SUBSCRIPTION_REQUIRED_ERROR_MESSAGE);
  const isDisabledForm = !!assignedUserId || !submittedEmailRequest || hasApiError;
  const isDisabledValidation = !!user || (assignedUserId && !isAvailableToAssign);
  const isDisabledAssign = !isAvailableToAssign || !submittedEmailRequest || hasApiError;

  const formik = useFormik({
    initialValues: ASSIGN_USER_INITIAL_VALUES,
    validationSchema: isDisabledValidation ? null : ASSIGN_USER_SCHEMA,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (assignedUserId) {
        assignDepositorMutate();

        return;
      }

      createDepositorMutate(values);
    },
  });

  const closeConfirmModal = () => onToggleConfirmModal(false);

  const assignmentStatus = assignmentStatusData?.body?.code;

  const openConfirmModal = async () => {
    if (assignmentStatusData?.body?.is_available_to_assign) {
      amplitudeService.logEvent(ASSIGN_AMPLITUDE_EVENTS_REDIRECT_MAP[assignmentStatus]);
    } else {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateDepositorRedirect);
    }

    const isValid = await isValidForm(formik);

    onToggleConfirmModal(isValid);
  };

  useEffect(() => {
    const assignmentStatusBody = assignmentStatusData?.body;

    if (assignmentStatusBody && !assignmentStatusBody?.is_available_to_assign) {
      amplitudeService.logEvent(ERROR_ASSIGN_AMPLITUDE_EVENTS_REDIRECT_MAP[assignmentStatusBody?.code]);
    }
  }, [assignmentStatusData?.body]);

  const searchUserEmail = (userId: string) => {
    formik.setErrors({});
    setAssignedUserId(userId);
    setSubmittedEmailRequest(true);
  };

  const submitUserEmail = () => {
    if (!formik.touched.email && formik.errors?.email) {
      formik.setTouched({ email: true });
    }
  };

  const onResetData = () => {
    setAssignedUserId(null);
    setSubmittedEmailRequest(false);
    resetCreateDepositor();
    resetAssignDepositor();
    formik.resetForm();
    formik.setFieldValue('email', formik.values.email);
  };

  useAssignUser(
    loadUserData?.body,
    formik,
    onResetData,
    assignedUserId,
    submittedEmailRequest,
    assignmentStatusData?.body?.is_available_to_assign,
    apiError
  );

  return (
    <AssignUserFormWrapper>
      <FormTitle>Assign Depositor</FormTitle>
      <FormMessage>Please indicate who will be making this receiving.</FormMessage>
      {formError && (
        <Notification variant='error' marginBottom='24px' marginTop='-10px'>
          {formError}
        </Notification>
      )}
      <FormikForm value={formik}>
        <>
          <SearchUserEmailInput
            role={USER_ROLE_MAP.DEPOSITOR}
            email={formik.values.email}
            onSearch={searchUserEmail}
            onSubmit={submitUserEmail}
            assignmentStatus={assignmentStatus}
            error={formik.errors?.email}
            apiError={apiError}
            disabled={isViewModeSubscription}
          />
        </>
        <FormikPhoneInput
          name='mobile_phone'
          label='Mobile Phone Number*'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='first_name'
          label='First Name*'
          placeholder='Enter First Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='middle_name'
          label='Middle Name'
          placeholder='Enter Middle Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='last_name'
          label='Last Name*'
          placeholder='Enter Last Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
      </FormikForm>
      <FormButtonBlock>
        <Button
          width={172}
          size='medium'
          onClick={openConfirmModal}
          disabled={isViewModeSubscription || isDisabledAssign}
        >
          Assign
        </Button>
      </FormButtonBlock>

      <ConfirmModal
        isOpen={isOpenConfirmModal}
        header='Confirm Wire Information'
        isLoading={isLoading}
        body={
          <>
            <ConfirmModalHint>
              You are certifying the validity of the wire information you have provided.
            </ConfirmModalHint>
            <ConfirmModalHint>
              This information will be committed to the WireVault blockchain and cannot be changed.
            </ConfirmModalHint>
            <ConfirmModalHint>You can cancel this wire until the receiving is sent.</ConfirmModalHint>
          </>
        }
        onClose={closeConfirmModal}
        onConfirm={formik.handleSubmit}
      />
      <SubscriptionRequiredModal
        isOpen={hasSubscriptionRequiredError}
        onClose={resetSubscriptionRequiredError}
        onConfirm={() => navigate(getAcceptMasterServiceAgreementPath())}
      >
        <li>Share your wire instructions with depositors now</li>
        <li>Add additional team members and administrators</li>
        <li>Access advanced ID verification tools</li>
      </SubscriptionRequiredModal>
    </AssignUserFormWrapper>
  );
};
